import Box from '@elementor/ui/Box';
import ErrorBoundary from '@elementor/ui/ErrorBoundary';
import { FocusTrap } from 'focus-trap-react';
import { useRef, useState, useEffect } from 'react';
import ScannerFooter from '@components/footer';
import ScannerHeader from '@components/header';
import ScannerTopBar from '@components/header/scanner-top-bar';
import { BLOCKS, SCANNER_ROOT_ID } from '@constants';
import { useScannerWizardContext } from '@context/scanner-wizard-context';
import {
	MainLayout,
	ColorContrastLayout,
	HeadingStructureLayout,
	ManualLayout,
	AltTextLayout,
	ErrorLayout,
	NoIssuesLayout,
} from '@layouts';
import MainLayoutLoader from '@layouts/main-layout/loader';
import {
	StyledHeaderWrapper,
	StyledScannerContainer,
} from '@styles/app.styles';
import { __ } from '@utils/i18n';

const Scanner = () => {
	const { violation, openedBlock, isError, proxyError, loading } =
		useScannerWizardContext();

	const containerRef = useRef(null);
	const [isScrolled, setIsScrolled] = useState(false);
	const shouldShowFooter =
		BLOCKS.main === openedBlock && !proxyError && !isError && !loading;

	useEffect(() => {
		const container = containerRef.current;

		if (!container) {
			return;
		}

		const handleScroll = () => {
			setIsScrolled(container.scrollTop > 0);
		};

		container.addEventListener('scroll', handleScroll);

		return () => {
			container.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const getMsgStateBlock = () => {
		if (proxyError) {
			return <Box />;
		}

		if (isError) {
			return <ErrorLayout />;
		}

		if (loading) {
			return <MainLayoutLoader />;
		}

		if (0 === violation) {
			return <NoIssuesLayout />;
		}

		return null;
	};

	const getIssuesBlock = () => {
		switch (openedBlock) {
			case BLOCKS.main:
				return <MainLayout />;
			case BLOCKS.altText:
				return <AltTextLayout />;
			case BLOCKS.colorContrast:
				return <ColorContrastLayout />;
			case BLOCKS.headingStructure:
				return <HeadingStructureLayout />;
			default:
				return <ManualLayout />;
		}
	};

	const HeaderWrapper = BLOCKS.main === openedBlock ? Box : StyledHeaderWrapper;

	return (
		<aside aria-label={__('Accessibility Assistant')} id={SCANNER_ROOT_ID}>
			<FocusTrap
				containerElements={[containerRef.current]}
				focusTrapOptions={{ initialFocus: false, allowOutsideClick: true }}
			>
				<StyledScannerContainer elevation={6} ref={containerRef}>
					<ErrorBoundary fallback={<ErrorLayout />}>
						<HeaderWrapper isScrolled={isScrolled}>
							<ScannerTopBar />

							<ScannerHeader />
						</HeaderWrapper>

						<Box>{getMsgStateBlock() || getIssuesBlock()}</Box>

						{shouldShowFooter && <ScannerFooter />}
					</ErrorBoundary>
				</StyledScannerContainer>
			</FocusTrap>
		</aside>
	);
};

export default Scanner;
